import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Input, Typography } from '../../../Material-UI/Components';
import APIService from '../../../service/APIService';
import { useLoader, useAppContext } from '../../../store/context';
import AuthShell from '../AuthShell';
import { getDashboardHomeUrl } from '../../../utils/appUrls';

const useStyles = makeStyles((theme) => ({
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  fieldWrap: {
    marginTop: 8,
  },
  input: {
    width: '100%',
    margin: '10px 0 !important',
    background: '#ffffff',
    '& .MuiOutlinedInput-root': {
      borderRadius: 18,
      background: '#f8fbff',
      minHeight: 56,
    },
  },
  primaryAction: {
    marginTop: '18px !important',
    minHeight: 52,
    borderRadius: 18,
    background: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)',
    boxShadow: '0 16px 28px rgba(14, 165, 233, 0.28)',
    color: '#ffffff',
    fontWeight: 700,
    letterSpacing: '0.08em',
    '&:hover': {
      background: 'linear-gradient(135deg, #075985 0%, #0284c7 100%)',
    },
  },
  helperRow: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  helperLink: {
    color: '#0369a1',
    cursor: 'pointer',
    fontWeight: 700,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  statusCard: {
    marginTop: 18,
    borderRadius: 18,
    border: '1px solid rgba(14, 165, 233, 0.18)',
    background: '#f0f9ff',
    padding: '14px 16px',
  },
  statusLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#0ea5e9',
  },
  statusText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 1.6,
    color: '#334155',
  },
  dividerLabel: {
    marginTop: 18,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#94a3b8',
  },
  error: {
    marginTop: 18,
    borderRadius: 18,
    border: '1px solid #fecaca',
    background: '#fff1f2',
    padding: '12px 14px',
    color: '#b91c1c',
    fontSize: 14,
    lineHeight: 1.6,
  },
}));

const Login = () => {
  const history = useHistory();
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const showLoader = useLoader();
  const { state } = useAppContext();
  const { email: signedMail } = state.user;
  const redirectPath = getSafeRedirectPath();

  if (signedMail && localStorage.getItem('analysis_user') && localStorage.getItem('chosenresume')) {
    window.location = redirectPath || '/track/summary';
  }

  const fetchData = () => {
    APIService.getResumes().then((resp) => setResumeState(resp.data));
  };

  const waitForAuthenticatedSession = async () => {
    const delays = [150, 300, 600, 1000, 1500];

    for (let index = 0; index < delays.length; index += 1) {
      try {
        const response = await APIService.isLoggedIn();
        if (response?.data?.email) {
          return response.data;
        }
      } catch (_error) {
        // Keep polling briefly while the auth cookie/session settles.
      }

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => window.setTimeout(resolve, delays[index]));
    }

    return null;
  };

  const setResumeState = (data) => {
    if (data) {
      localStorage.setItem('chosenresume', JSON.stringify(data));
      if (data[0] && data[0].fullresponse) {
        const dfull = JSON.parse(data[0].fullresponse);
        localStorage.setItem('analysis_user', JSON.stringify(dfull));
      }
    }
    if (data[0] && data[0].selectedhard) localStorage.setItem('selectedhard', JSON.stringify(data[0].selectedhard));
    if (data[0] && data[0].selectedsoft) localStorage.setItem('selectedsosft', JSON.stringify(data[0].selectedsoft));
    if (data[0] && data[0].hardkill) localStorage.setItem('hardskill', JSON.stringify(data[0].hardskill));
    if (data[0] && data[0].softkill) localStorage.setItem('softskill', JSON.stringify(data[0].softskill));
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    setError('');
    showLoader(true);
    APIService.login(email, password)
      .then(async () => {
        await waitForAuthenticatedSession();
        fetchData();
        window.location = redirectPath || getDashboardHomeUrl();
      })
      .catch(() => setError('Authentication failed. Check your email and password, then try again.'))
      .finally(() => showLoader(false));
  };

  return (
    <AuthShell
      pageTitle="Login | MyJobAide"
      canonicalPath="/track/login"
      robotsContent="noindex,follow"
      eyebrow="Career Operating System"
      heroTitle="Pick up your career system where you left off."
      heroSubtitle="Sign in to continue your strategy, execution, interview preparation, and progress tracking in one place."
      heroBadges={[
        {
          label: 'Current system',
          value: 'Signals, blockers, strategy',
          hint: 'Your latest guidance and execution context stay aligned after sign-in.',
        },
        {
          label: 'Resume to outcomes',
          value: 'One connected workflow',
          hint: 'Move from stronger materials to clearer next steps and better conversion.',
        },
      ]}
      heroQuoteTitle="Why sign in"
      heroQuoteBody="MyJobAide works best when your targeting, execution, and interview prep share one source of truth. Signing in restores that full context."
      formEyebrow="Welcome back"
      formTitle="Sign in to MyJobAide"
      formSubtitle="Continue from your latest saved profile and keep your career operating system in sync."
      footerPrompt="Don't have an account yet?"
      footerActionLabel="Create one now"
      onFooterAction={() => history.push(redirectPath ? `/signup?redirect=${encodeURIComponent(redirectPath)}` : '/signup')}
    >
      <form className={classes.loginForm} onSubmit={handleSubmit}>
        <div className={classes.fieldWrap}>
          <Input
            className={classes.input}
            type="text"
            label="Email"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            required
            fullWidth
          />
        </div>
        <div className={classes.fieldWrap}>
          <Input
            className={classes.input}
            type="password"
            label="Password"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            required
            fullWidth
          />
        </div>

        <div className={classes.statusCard}>
          <div className={classes.statusLabel}>What resumes after sign-in</div>
          <div className={classes.statusText}>
            Your latest profile, execution state, interview history, and next recommended steps will load together.
          </div>
        </div>

        <div className={classes.dividerLabel}>Secure workspace access</div>

        <Button
          name="Login"
          type="submit"
          className={classes.primaryAction}
          fullWidth
          size="large"
        >
          Continue to workspace
        </Button>

        <div className={classes.helperRow}>
          <Typography variant="body2" color="textSecondary">
            Need help getting back in?
          </Typography>
          <span onClick={() => history.push('/forgot')} className={classes.helperLink}>
            Forgot password
          </span>
        </div>

        {error ? <div className={classes.error}>{error}</div> : null}
      </form>
    </AuthShell>
  );
};

export default Login;

function getSafeRedirectPath() {
  if (typeof window === 'undefined') {
    return '';
  }

  const searchParams = new URLSearchParams(window.location.search);
  const redirect =
    searchParams.get('redirect') ||
    searchParams.get('redirectafterlogin') ||
    '';
  if (!redirect.startsWith('/') || redirect.startsWith('//')) {
    return '';
  }

  return redirect;
}
